import React, { useState, useEffect } from "react";
import { SparklesIcon, PlusIcon } from "@heroicons/react/24/outline";
import type { ObjectivesPhaseProps } from "./objectivesPhase.types";
import { ObjectivesList, ObjectivesSkeleton } from "./components";
import { useGeminiMutations } from "../../../../hooks/data/useProjectMutations";
import { Button } from "@/components";

export default function ObjectivesPhase({ project, onSave, onNext }: ObjectivesPhaseProps) {
  const [objectives, setObjectives] = useState<string[]>(project.objectives || []);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { generateObjectives, isGeneratingObjectives } = useGeminiMutations();

  // Detectar mudanças nos objetivos
  useEffect(() => {
    const currentObjectives = project.objectives || [];
    const hasChanges = 
      objectives.length !== currentObjectives.length ||
      objectives.some((obj, index) => obj !== currentObjectives[index]);
    setHasUnsavedChanges(hasChanges);
  }, [objectives, project.objectives]);

  const handleGenerateObjectives = async () => {
    if (!project.description) {
      return;
    }

    try {
      const generatedObjectives = await generateObjectives({
        projectId: project.id,
        description: project.description,
      });
      
      setObjectives(generatedObjectives);
    } catch (error) {
      console.error("Erro ao gerar objetivos:", error);
    }
  };

  const handleAddObjective = () => {
    const newObjective = "";
    setObjectives([...objectives, newObjective]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(objectives.filter(obj => obj.trim() !== ""));
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Erro ao salvar objetivos:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (hasUnsavedChanges) {
      await handleSave();
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold font-montserrat text-raro-gray-900">
            Objetivos do Projeto
          </h2>
          <p className="text-sm text-raro-gray-600 mt-1">
            Defina objetivos SMART (Específicos, Mensuráveis, Atingíveis, Relevantes, Temporais)
          </p>
        </div>

        {!objectives.length && (
          <Button
            onClick={handleGenerateObjectives}
            disabled={isGeneratingObjectives}
            className="bg-primary-600 text-white hover:bg-primary-700"
          >
            {isGeneratingObjectives ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                Gerando...
              </>
            ) : (
              <>
                <SparklesIcon className="w-4 h-4 mr-2" />
                Gerar com IA
              </>
            )}
          </Button>
        )}
      </div>

      {/* Objectives Content */}
      {objectives.length > 0 || isGeneratingObjectives ? (
        <div className="space-y-4">
          {/* Actions Bar - Only show when not generating */}
          {!isGeneratingObjectives && (
            <div className="flex items-center justify-between p-4 bg-raro-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <Button
                  onClick={handleGenerateObjectives}
                  disabled={isGeneratingObjectives}
                  variant="secondary"
                  size="sm"
                >
                  <SparklesIcon className="w-4 h-4 mr-2" />
                  Regenerar com IA
                </Button>

                <Button
                  onClick={handleAddObjective}
                  variant="secondary"
                  size="sm"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Adicionar Objetivo
                </Button>
              </div>

              <div className="text-sm text-raro-gray-600">
                {objectives.length} objetivo{objectives.length !== 1 ? "s" : ""}
              </div>
            </div>
          )}

          {/* Loading State for Generation */}
          {isGeneratingObjectives ? (
            <ObjectivesSkeleton count={4} />
          ) : (
            <ObjectivesList 
              objectives={objectives}
              onUpdate={setObjectives}
              isGenerating={false}
            />
          )}
        </div>
      ) : (
        <div className="text-center py-12 bg-raro-gray-50 rounded-lg">
          <SparklesIcon className="w-12 h-12 text-raro-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-raro-gray-900 mb-2">
            Nenhum objetivo definido
          </h3>
          <p className="text-raro-gray-600 mb-6 max-w-md mx-auto">
            Use a IA para gerar objetivos SMART baseados na descrição do projeto ou adicione manualmente.
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button
              onClick={handleGenerateObjectives}
              disabled={isGeneratingObjectives}
              className="bg-primary-600 text-white hover:bg-primary-700"
            >
              {isGeneratingObjectives ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Gerando objetivos...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-4 h-4 mr-2" />
                  Gerar com IA
                </>
              )}
            </Button>
            <Button
              onClick={handleAddObjective}
              variant="secondary"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Criar manualmente
            </Button>
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-raro-gray-200">
        <div className="text-sm text-raro-gray-600">
          {hasUnsavedChanges && "• Você tem alterações não salvas"}
        </div>
        
        <div className="flex items-center space-x-3">
          {hasUnsavedChanges && (
            <Button
              onClick={handleSave}
              disabled={isSaving}
              variant="secondary"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  Salvando...
                </>
              ) : (
                "Salvar"
              )}
            </Button>
          )}
          
          <Button
            onClick={handleNext}
            disabled={isSaving || isGeneratingObjectives}
            className="bg-primary-600 text-white hover:bg-primary-700"
          >
            Próxima Fase
          </Button>
        </div>
      </div>
    </div>
  );
}